import type { MenuItem, Order, OrderItem, OrderStatus, PaymentItemAllocation } from '../types';

export type MenuPriceLookup = Pick<MenuItem, 'id' | 'name' | 'price' | 'categoryId' | 'imageUrl' | 'imageUrlThumb'>;
import { isUnpaidOnlineWebOrder } from './onlineOrderVisibility';
import { orderCreatedAtMs, toMillis, ymdInTimeZone } from './orderTimestamps';

/** Use only quantityCompleted for per-card completion logic. Never infer from status. */
export function getEffectiveQuantityCompleted(item: Order['items'][0]): number {
  return item.quantityCompleted ?? 0;
}

/**
 * Units already marked done in kitchen — floor for POS qty cuts.
 * Prefer quantityCompleted. Legacy `status === 'completed'` without a count:
 * use `priorQuantity` when the line qty may have grown locally; otherwise the
 * current qty (fully done line with no explicit count).
 */
export function getKitchenCompletedFloor(
  item: Pick<Order['items'][0], 'quantity' | 'quantityCompleted' | 'status'>,
  options?: { priorQuantity?: number },
): number {
  const qty = Number(item.quantity ?? 0);
  const done = Number(item.quantityCompleted ?? 0);
  if (done > 0) return Math.min(done, Math.max(0, qty));
  if (item.status === 'completed') {
    const prior = options?.priorQuantity;
    if (typeof prior === 'number' && prior >= 0) {
      return Math.min(prior, Math.max(0, qty));
    }
    return Math.max(0, qty);
  }
  return 0;
}

/** True when kitchen has finished every unit on this line (qty may still grow later). */
export function isLineFullyKitchenDone(
  item: Pick<Order['items'][0], 'quantity' | 'quantityCompleted' | 'status'>,
  options?: { priorQuantity?: number },
): boolean {
  const qty = Number(item.quantity ?? 0);
  if (qty <= 0) return item.status === 'completed';
  return getKitchenCompletedFloor(item, options) + 0.001 >= qty;
}

/** Per-card served: when quantityServedByBatch exists, use it so serving one card doesn't mark the other. */
export function getEffectiveQuantityServedForBatch(
  item: Order['items'][0],
  batchIndex: number | undefined,
  _targetQty: number
): number {
  // When we have per-batch served data, always use it for that specific card.
  if (batchIndex != null && item.quantityServedByBatch && typeof item.quantityServedByBatch === 'object') {
    const perBatch = item.quantityServedByBatch[String(batchIndex)];
    if (typeof perBatch === 'number') return perBatch;
  }

  // For running-order cards (batchIndex >= 1), never fall back to global served,
  // otherwise previously served quantities from earlier batches can make a fresh
  // running card appear fully served as soon as it's created.
  if (batchIndex != null && batchIndex >= 1) {
    return 0;
  }

  // For the base card (no batchIndex / legacy orders), fall back to the legacy
  // per-item served fields so older data without quantityServedByBatch still works.
  return item.quantityServed ?? (item.served ? item.quantity : 0);
}

/** Per-card completion: when quantityCompletedByBatch exists, use it so running-first doesn't mark main done. */
export function getEffectiveQuantityCompletedForBatch(
  item: Order['items'][0],
  batchIndex: number | undefined,
  _targetQty: number
): number {
  if (batchIndex != null && item.quantityCompletedByBatch && typeof item.quantityCompletedByBatch === 'object') {
    const perBatch = item.quantityCompletedByBatch[String(batchIndex)];
    if (typeof perBatch === 'number') return perBatch;
    if (batchIndex >= 1) return 0;
  }
  if (batchIndex != null && batchIndex >= 1) return 0;
  return item.quantityCompleted ?? 0;
}

const isDiscountItem = (item: Order['items'][0]) =>
  (typeof item.price === 'number' && item.price < 0) ||
  String(item.id).includes('combo-discount');

/**
 * Compute the First Table discount for an order based on currently stored order fields.
 * Assumes firstTableDiscountAmount has been computed and stored on the order by POS logic.
 */
export function getFirstTableDiscount(order: Order): number {
  if (order.discountMethod !== 'first-table') return 0;
  return Math.max(0, order.firstTableDiscountAmount ?? 0);
}

/**
 * Get items belonging to a card (base or running batch) and their completion targets.
 * Returns { items: [{ item, originalIndex, targetQty }], allCompleted, allServed }
 */
export function getCardItemsWithTargets(
  order: Order,
  batchIndex: number | undefined
): {
  items: { item: Order['items'][0]; originalIndex: number; targetQty: number; baselineQty?: number }[];
  allCompleted: boolean;
  allServed: boolean;
} {
  const items = order.items ?? [];
  const batches = order.orderBatches ?? [];
  // For KDS/service base quantities, prefer the frozen initialKitchenSnapshot;
  // fall back to lastSentItemSnapshot for older orders.
  const snapshot = order.initialKitchenSnapshot ?? order.lastSentItemSnapshot ?? [];
  const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));
  const initialQty = new Map<string, number>();
  items.forEach((item, idx) => {
    if (item.removed) return;
    const lineId = item.lineId ?? `idx-${idx}`;
    const prev = item.lineId ? snapshotByLineId.get(item.lineId) : snapshot[idx];
    if (prev) initialQty.set(lineId, prev.quantity);
  });

  let cardItems: { item: Order['items'][0]; originalIndex: number; targetQty: number }[] = [];
  if (batchIndex == null) {
    const initialBatch = batches[0];
    const allLineIndices = items.map((_, i) => i);
    const activeKitchenIndices = allLineIndices.filter((idx) => {
      const item = items[idx];
      return !!item && !isDiscountItem(item) && !item.removed && !item.isBuffetPackage && !item.isMealPlanSale;
    });
    let indices: number[];
    if (initialBatch?.itemIndices?.length) {
      const fromBatch = initialBatch.itemIndices.filter((idx) => idx >= 0 && idx < items.length);
      const batchSet = new Set(fromBatch);
      /** POS can append lines after batch 0 was written; stale itemIndices would hide them on KDS. */
      const batchMissesActiveLine = activeKitchenIndices.some((idx) => !batchSet.has(idx));
      const hasRunningKitchenBatch = batches.length > 1;

      if (!batchMissesActiveLine) {
        indices = fromBatch;
      } else if (!hasRunningKitchenBatch) {
        // Single-card order: keep showing every active line if batch 0 metadata is stale.
        indices = allLineIndices;
      } else {
        /**
         * Running batch exists: never expand main card to `allLineIndices` — that duplicates
         * new lines on both base and running tickets. Only merge indices for lines that were
         * on the initial kitchen snapshot (same lineId or legacy index row).
         */
        const merged = new Set(fromBatch);
        for (const idx of activeKitchenIndices) {
          if (merged.has(idx)) continue;
          const item = items[idx];
          if (!item || isDiscountItem(item) || item.removed) continue;
          const prev = item.lineId ? snapshotByLineId.get(item.lineId) : snapshot[idx];
          if (prev) merged.add(idx);
        }
        indices = Array.from(merged).sort((a, b) => a - b);
      }
    } else {
      indices = allLineIndices;
    }
    cardItems = indices
      .map((idx) => {
        const item = items[idx];
        if (!item || isDiscountItem(item) || item.removed) return null;
        const lineId = item.lineId ?? `idx-${idx}`;
        const targetQty = initialQty.get(lineId) ?? item.quantity;
        return { item, originalIndex: idx, targetQty };
      })
      .filter((x): x is NonNullable<typeof x> => x != null);
  } else if (batchIndex >= 1 && batches[batchIndex]) {
    const batch = batches[batchIndex];
    const deltaMap = new Map<number, number>();
    (batch.deltas ?? []).forEach((d) => {
      if (typeof d.index === 'number' && typeof d.deltaQty === 'number' && d.deltaQty > 0) {
        deltaMap.set(d.index, d.deltaQty);
      }
    });
    const hasExplicitDeltas = deltaMap.size > 0;
    const getBaseline = (idx: number, lineId: string) => {
      let base = initialQty.get(lineId) ?? 0;
      for (let b = 1; b < batchIndex; b++) {
        const d = batches[b]?.deltas?.find((x) => x.index === idx);
        if (d && d.deltaQty > 0) base += d.deltaQty;
      }
      return base;
    };
    const rowForIndex = (idx: number) => {
      const item = items[idx];
      if (!item || isDiscountItem(item) || item.removed) return null;
      const lineId = item.lineId ?? `idx-${idx}`;
      const baseline = getBaseline(idx, lineId);
      /**
       * Running-order cards must only show true batch deltas.
       * When deltas are present, do not infer from current quantity-baseline,
       * otherwise unchanged base lines can reappear on running cards.
       * Cap by live item.quantity so a later POS qty cut shrinks/hides this card.
       */
      const rawDelta = hasExplicitDeltas
        ? (deltaMap.get(idx) ?? 0)
        : Math.max(0, item.quantity - baseline);
      const remainingAboveBaseline = Math.max(0, item.quantity - baseline);
      const deltaQty = Math.min(rawDelta, remainingAboveBaseline);
      const targetQty = deltaQty;
      if (targetQty <= 0) return null;
      return { item, originalIndex: idx, targetQty, baselineQty: baseline };
    };
    const fromBatchIndices = batch.itemIndices.filter((idx) => idx >= 0 && idx < items.length);
    const sourceIndices = hasExplicitDeltas
      ? Array.from(new Set([...fromBatchIndices, ...Array.from(deltaMap.keys())]))
      : fromBatchIndices;
    cardItems = sourceIndices
      .map(rowForIndex)
      .filter((x): x is NonNullable<typeof x> => x != null)
      .sort((a, b) => a.originalIndex - b.originalIndex);
  }

  if (cardItems.length === 0) {
    return { items: [], allCompleted: true, allServed: true };
  }

  const cardBatchIndex = batchIndex != null ? batchIndex : 0;

  const allCompleted = cardItems.every(
    ({ item, targetQty }) =>
      getEffectiveQuantityCompletedForBatch(item, cardBatchIndex, targetQty) >= targetQty
  );

  // For per-card "all served", use per-batch served quantities when available so that
  // a newly created running-order card is not treated as fully served just because the
  // base card (or a previous batch) was already served.
  const allServed = cardItems.every(
    ({ item, targetQty }) =>
      getEffectiveQuantityServedForBatch(item, cardBatchIndex, targetQty) >= targetQty
  );

  return { items: cardItems, allCompleted, allServed };
}

/**
 * After a POS qty cut, rewrite running-order batch deltas so they cannot exceed
 * remaining quantity above the initial kitchen baseline. Drops empty running batches.
 */
export function capRunningOrderBatchDeltasToItems(
  order: Pick<Order, 'initialKitchenSnapshot' | 'lastSentItemSnapshot'>,
  items: Order['items'],
  batches: NonNullable<Order['orderBatches']> | undefined,
): NonNullable<Order['orderBatches']> | undefined {
  if (!batches || batches.length <= 1) return batches;
  const snapshot = order.initialKitchenSnapshot ?? order.lastSentItemSnapshot ?? [];
  const snapshotByLineId = new Map(snapshot.map((s) => [s.lineId, s]));

  const next: NonNullable<Order['orderBatches']> = [batches[0]];
  for (let b = 1; b < batches.length; b++) {
    const batch = batches[b];
    const cappedDeltas: NonNullable<NonNullable<Order['orderBatches']>[number]['deltas']> = [];
    const deltaSources = batch.deltas?.length
      ? batch.deltas
      : (batch.itemIndices ?? []).map((index) => ({ index, deltaQty: 0 }));

    for (const d of deltaSources) {
      const idx = d.index;
      const item = items[idx];
      if (!item || item.removed) continue;
      const lineId = item.lineId ?? `idx-${idx}`;
      let baseline = item.lineId
        ? (snapshotByLineId.get(item.lineId)?.quantity ?? 0)
        : (snapshot[idx]?.quantity ?? 0);
      for (let pb = 1; pb < next.length; pb++) {
        const prior = next[pb]?.deltas?.find((x) => x.index === idx);
        if (prior && prior.deltaQty > 0) baseline += prior.deltaQty;
      }
      const remaining = Math.max(0, item.quantity - baseline);
      const raw = typeof d.deltaQty === 'number' && d.deltaQty > 0
        ? d.deltaQty
        : remaining;
      const deltaQty = Math.min(raw, remaining);
      if (deltaQty > 0) cappedDeltas.push({ index: idx, deltaQty });
    }

    if (cappedDeltas.length === 0) continue;
    next.push({
      ...batch,
      itemIndices: cappedDeltas.map((d) => d.index),
      deltas: cappedDeltas,
    });
  }
  return next;
}

export function getCardCompletionStatus(
  order: Order,
  batchIndex: number | undefined
): { allItemsCompleted: boolean; allItemsServed: boolean } {
  const { allCompleted, allServed } = getCardItemsWithTargets(order, batchIndex);
  return { allItemsCompleted: allCompleted, allItemsServed: allServed };
}

/**
 * Compute order total from current items (exclude removed) and discounts.
 * Matches PrintableReceipt so app display and receipt never disagree.
 */
export function getOrderTotalFromItems(order: Order): number {
  const items = coerceOrderItemsArray(order.items);
  const totalBeforeDiscount = items.reduce(
    (sum, item) => (item.removed ? sum : sum + item.price * item.quantity),
    0
  );
  const rewardDiscount = order.appliedReward ? order.appliedReward.discountValue : 0;
  const method = order.discountMethod ?? 'manual';
  const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
  const firstTableDiscount = method === 'first-table' ? getFirstTableDiscount(order) : 0;
  const couponDiscount = order.couponDiscountAmount ?? 0;
  const residentDiscount = order.residentDiscountAmount ?? 0;
  const itemsNet =
    totalBeforeDiscount - rewardDiscount - manualDiscount - firstTableDiscount - couponDiscount - residentDiscount;
  const deliveryCharge =
    order.orderType === 'delivery' ? Math.max(0, order.deliveryCharge ?? 0) : 0;
  const holidaySurcharge = Math.max(0, order.holidaySurchargeAmount ?? 0);
  return Math.round((itemsNet + deliveryCharge + holidaySurcharge) * 100) / 100;
}

/** True when the order has any order-level discount (manual, First Table, coupon, resident, or reward). */
export function orderHasAppliedDiscount(order: Order): boolean {
  const method = order.discountMethod ?? 'manual';
  const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
  const firstTableDiscount = method === 'first-table' ? getFirstTableDiscount(order) : 0;
  const couponDiscount = order.couponDiscountAmount ?? 0;
  const residentDiscount = order.residentDiscountAmount ?? 0;
  const rewardDiscount = order.appliedReward?.discountValue ?? 0;
  return (
    manualDiscount > 0 ||
    firstTableDiscount > 0 ||
    couponDiscount > 0 ||
    residentDiscount > 0 ||
    rewardDiscount > 0
  );
}

/**
 * NZ retail: menu line prices are GST-**inclusive** (same as POS receipts).
 * Split into ex-GST subtotal and GST portion using the 15/115 factor.
 */
export function totalsFromGstInclusiveItemSum(itemsTotalInclGst: number): {
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
} {
  const totalAmount = Math.round(itemsTotalInclGst * 100) / 100;
  const taxAmount = Math.round(totalAmount * (15 / 115) * 100) / 100;
  const subtotal = Math.round((totalAmount - taxAmount) * 100) / 100;
  return { subtotal, taxAmount, totalAmount };
}

/** Cart / quote lines with GST-inclusive unit prices (no order-level discounts). */
export function totalsFromGstInclusiveLineItems(
  lines: ReadonlyArray<{ price: number; quantity: number }>
): { subtotal: number; taxAmount: number; totalAmount: number } {
  const sum = lines.reduce((s, l) => s + (Number(l.price) || 0) * (Number(l.quantity) || 0), 0);
  return totalsFromGstInclusiveItemSum(sum);
}

/**
 * Compute subtotal, taxAmount, totalAmount for an order.
 * For Uber/DoorDash, uses stored totals when present (they include platform %).
 * For all other order types, always computes from current items (excl. removed) so display
 * and receipts stay correct after item add/remove, even if stored totals were stale.
 */
export function getOrderTotals(order: Order): { subtotal: number; taxAmount: number; totalAmount: number } {
  const useStored =
    order.orderType === 'uber-doordash' &&
    order.totalAmount != null &&
    order.subtotal != null &&
    order.taxAmount != null &&
    !Number.isNaN(order.totalAmount) &&
    order.totalAmount >= 0;
  if (useStored) {
    return {
      subtotal: order.subtotal,
      taxAmount: order.taxAmount,
      totalAmount: order.totalAmount,
    };
  }
  const totalAmount = getOrderTotalFromItems(order);
  const taxAmount = Math.round(totalAmount * (15 / 115) * 100) / 100;
  const subtotal = Math.round((totalAmount - taxAmount) * 100) / 100;
  return { subtotal, taxAmount, totalAmount };
}

/** Latest payment time, else order creation time. Matches Detailed Reports → Trends & Forecast day bucketing. */
export function getOrderSaleTimestampForReporting(order: Order): number {
  if (order.payments?.length) {
    return Math.max(...order.payments.map((p) => p.timestamp));
  }
  return order.timestamp;
}

/** YYYY-MM-DD in the given IANA timezone (e.g. Pacific/Auckland). */
export function getOrderSaleDateKeyInTimeZone(order: Order, timeZone: string): string {
  const ts = getOrderSaleTimestampForReporting(order);
  return new Date(ts).toLocaleDateString('en-CA', { timeZone });
}

/** Sum of completed payments (exclude pending). */
export function getAmountPaid(order: Order): number {
  return Math.round(
    (order.payments ?? [])
      .filter((p) => p.paymentStatus !== 'pending')
      .reduce((sum, p) => sum + p.amount, 0) * 100
  ) / 100;
}

/**
 * True if order has a balance due. Returns true when EITHER:
 *   - dollar balance (computed total - payments) is > 0.01, OR
 *   - any non-removed line still has unpaid quantity (catches floating-point drift on itemized
 *     sub-bill payments where dollar sum reconciles but a line shows 0/3 paid).
 */
export function hasBalanceDue(order: Order): boolean {
  const { totalAmount } = getOrderTotals(order);
  const paid = getAmountPaid(order);
  if (totalAmount - paid > 0.01) return true;
  if (hasUnpaidItems(order)) return true;
  return false;
}

// ---------------------------------------------------------------------------
// Sub-bill / itemized payment helpers
// ---------------------------------------------------------------------------

/** Stable lineId helper: returns item.lineId, falling back to a synthetic one based on array index. */
export function getItemLineId(item: OrderItem, index: number): string {
  return item.lineId ?? `idx-${index}`;
}

/** True for combo-discount/negative-price helper lines that should not be selectable on their own. */
export function isOrderItemDiscountLine(item: OrderItem): boolean {
  if (typeof item.price === 'number' && item.price < 0) return true;
  if (typeof item.id === 'string' && (item.id as string).includes('combo-discount')) return true;
  return false;
}

/** Quantity of a line that has already been settled by an itemized sub-bill payment. */
export function getPaidQuantityForItem(item: OrderItem): number {
  const direct = item.paidQuantity ?? 0;
  if (direct > 0) return direct;
  // Defensive fallback: derive from per-payment map if direct counter wasn't written.
  const map = item.paidQuantityByPaymentId;
  if (map && typeof map === 'object') {
    let sum = 0;
    for (const k of Object.keys(map)) {
      const v = map[k];
      if (typeof v === 'number' && v > 0) sum += v;
    }
    return sum;
  }
  return 0;
}

/** Quantity of a line still available to be paid (clamped to 0). */
export function getUnpaidQuantityForItem(item: OrderItem): number {
  if (item.removed) return 0;
  const total = item.quantity ?? 0;
  const paid = getPaidQuantityForItem(item);
  return Math.max(0, total - paid);
}

/**
 * True if this order is using itemized (sub-bill) payment tracking.
 *
 * Detected by ANY of:
 *   - At least one item has `paidQuantity > 0` or a non-empty `paidQuantityByPaymentId` map.
 *   - Order has one or more saved sub-bills.
 *   - Any prior payment carried `itemAllocations`.
 *
 * Important for `hasUnpaidItems` / `hasBalanceDue`: if an order has NEVER used itemized payments,
 * the per-line `paidQuantity` counter will always be 0 even after a full dollar payment, so we
 * MUST NOT use line-level paid quantity to decide whether the order is settled — the dollar
 * balance is the only source of truth for those orders.
 */
export function isOrderItemized(order: Order): boolean {
  if (Array.isArray(order.subBills) && order.subBills.length > 0) return true;
  if (Array.isArray(order.payments) && order.payments.some((p) => Array.isArray(p.itemAllocations) && p.itemAllocations.length > 0)) return true;
  const items = order.items ?? [];
  for (const item of items) {
    if ((item.paidQuantity ?? 0) > 0) return true;
    const map = item.paidQuantityByPaymentId;
    if (map && typeof map === 'object' && Object.keys(map).length > 0) return true;
  }
  return false;
}

/**
 * True when at least one non-removed, non-discount line still has unpaid quantity.
 *
 * Returns `false` for orders that have NEVER been touched by itemized payments — for those orders,
 * line-level paid quantities are not maintained and the dollar balance is authoritative. This
 * prevents regular full-amount card/cash payments from being incorrectly flagged as "still has
 * unpaid items" just because `paidQuantity` defaulted to 0.
 */
export function hasUnpaidItems(order: Order): boolean {
  if (!isOrderItemized(order)) return false;
  const items = order.items ?? [];
  for (const item of items) {
    if (item.removed) continue;
    if (isOrderItemDiscountLine(item)) continue;
    if (getUnpaidQuantityForItem(item) > 0) return true;
  }
  return false;
}

/** Selection request from the SubBillBuilder: which lineIds and how many of each to pay for. */
export interface SubBillSelection {
  lineId: string;
  quantity: number;
}

export type SubBillDiscountAllocationMode = 'proportional' | 'remainder';

export interface SubBillTotals {
  /** Per-line allocations with money breakdown; ready to attach to a Payment.itemAllocations. */
  allocations: PaymentItemAllocation[];
  /** Sum of selected items' face value (price * qty, GST inclusive). */
  itemsSubtotal: number;
  /** Combined discount applied to this sub-bill (0 in remainder mode). */
  allocatedDiscount: number;
  /** Combined delivery share applied to this sub-bill (0 in remainder mode). */
  allocatedDelivery: number;
  /** Combined holiday surcharge share applied to this sub-bill (0 in remainder mode). */
  allocatedHoliday: number;
  /** GST portion of the sub-bill total (15/115 method). */
  gst: number;
  /** Net subtotal (excl. GST). */
  netSubtotal: number;
  /** Final money owed for this sub-bill (sum of allocations.allocatedTotal, rounded). */
  total: number;
  /** True if any selection exceeded the unpaid quantity for that line — the caller should refuse. */
  hasOverflow: boolean;
}

/**
 * Compute money breakdown for a sub-bill selection. Single source of truth used by both the
 * SubBillBuilder UI (preview) and the server-side validator in handleProcessPayment.
 *
 * Discount allocation modes:
 *  - 'proportional': each selected line gets a share of the order's discount/delivery proportional
 *    to its share of the order subtotal-before-discount. Fair across customers.
 *  - 'remainder': sub-bill pays face value (incl. GST); discount/delivery stay on the remaining balance.
 */
export function computeSubBillTotals(
  order: Order,
  selections: SubBillSelection[],
  mode: SubBillDiscountAllocationMode = 'proportional'
): SubBillTotals {
  const items = order.items ?? [];
  const itemByLineId = new Map<string, { item: OrderItem; index: number }>();
  items.forEach((it, idx) => {
    itemByLineId.set(getItemLineId(it, idx), { item: it, index: idx });
  });

  // Total order discount (rewards, manual, first-table, coupon, resident)
  const rewardDiscount = order.appliedReward ? order.appliedReward.discountValue : 0;
  const method = order.discountMethod ?? 'manual';
  const manualDiscount = method === 'manual' ? order.manualDiscount ?? 0 : 0;
  const firstTableDiscount = method === 'first-table' ? getFirstTableDiscount(order) : 0;
  const couponDiscount = order.couponDiscountAmount ?? 0;
  const residentDiscount = order.residentDiscountAmount ?? 0;
  const totalOrderDiscount =
    rewardDiscount + manualDiscount + firstTableDiscount + couponDiscount + residentDiscount;

  // Order delivery (manual delivery only)
  const totalOrderDelivery =
    order.orderType === 'delivery' ? Math.max(0, order.deliveryCharge ?? 0) : 0;
  const totalOrderHoliday = Math.max(0, order.holidaySurchargeAmount ?? 0);

  // Order's total face value (sum of price * quantity for active, non-discount lines).
  // Used only to compute proportional shares so each customer's allocated discount sums to the whole.
  const orderItemsFaceValue = items.reduce((sum, it) => {
    if (it.removed) return sum;
    if (isOrderItemDiscountLine(it)) return sum;
    return sum + it.price * it.quantity;
  }, 0);

  let hasOverflow = false;
  const allocations: PaymentItemAllocation[] = [];

  for (const sel of selections) {
    const found = itemByLineId.get(sel.lineId);
    if (!found) continue;
    const { item } = found;
    if (item.removed) continue;
    if (isOrderItemDiscountLine(item)) continue;
    const qty = Math.max(0, Math.floor(sel.quantity || 0));
    if (qty <= 0) continue;
    if (qty > getUnpaidQuantityForItem(item)) {
      hasOverflow = true;
      continue;
    }

    const lineSubtotal = round2(item.price * qty);

    let allocatedDiscount = 0;
    let allocatedDelivery = 0;
    let allocatedHoliday = 0;
    if (mode === 'proportional' && orderItemsFaceValue > 0) {
      const share = (item.price * qty) / orderItemsFaceValue;
      allocatedDiscount = round2(totalOrderDiscount * share);
      allocatedDelivery = round2(totalOrderDelivery * share);
      allocatedHoliday = round2(totalOrderHoliday * share);
    }

    const allocatedTotalRaw = lineSubtotal - allocatedDiscount + allocatedDelivery + allocatedHoliday;
    const allocatedTotal = round2(allocatedTotalRaw);
    const allocatedTax = round2(allocatedTotal * (15 / 115));

    allocations.push({
      lineId: sel.lineId,
      quantity: qty,
      lineSubtotal,
      allocatedDiscount,
      allocatedDelivery,
      allocatedHoliday,
      allocatedTax,
      allocatedTotal,
    });
  }

  const itemsSubtotal = round2(allocations.reduce((s, a) => s + a.lineSubtotal, 0));
  const allocatedDiscount = round2(allocations.reduce((s, a) => s + a.allocatedDiscount, 0));
  const allocatedDelivery = round2(allocations.reduce((s, a) => s + a.allocatedDelivery, 0));
  const allocatedHoliday = round2(allocations.reduce((s, a) => s + (a.allocatedHoliday ?? 0), 0));
  const total = round2(allocations.reduce((s, a) => s + a.allocatedTotal, 0));
  const gst = round2(total * (15 / 115));
  const netSubtotal = round2(total - gst);

  return {
    allocations,
    itemsSubtotal,
    allocatedDiscount,
    allocatedDelivery,
    allocatedHoliday,
    gst,
    netSubtotal,
    total,
    hasOverflow,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * True if order has at least one real line item (not removed, not discount-only, quantity > 0).
 * Used for: floor plan occupancy, table status sync, Pending list — so empty/ghost orders don't mark tables or show in Pending.
 */
function getMaxBatchItemIndex(order: Order): number {
  return (
    order.orderBatches?.reduce((max, batch) => {
      const batchMax = (batch.itemIndices ?? []).reduce((m, i) => Math.max(m, i), -1);
      return Math.max(max, batchMax);
    }, -1) ?? -1
  );
}

function normalizeItemName(name: unknown): string {
  return String(name ?? '')
    .toLowerCase()
    .trim();
}

function snapshotAlignsWithItemsByIndex(
  items: Order['items'],
  snapshot: NonNullable<Order['lastSentItemSnapshot']>,
): boolean {
  if (!snapshot.length || items.length !== snapshot.length) return false;
  for (let i = 0; i < snapshot.length; i++) {
    const snapName = normalizeItemName(snapshot[i]?.name);
    const itemName = normalizeItemName(items[i]?.name);
    if (!snapName || !itemName || snapName !== itemName) return false;
    const snapQty = snapshot[i]?.quantity ?? 1;
    const itemQty = items[i]?.quantity ?? 1;
    if (snapQty !== itemQty && items[i]?.removed !== true) return false;
  }
  return true;
}

/** Richness score for choosing between a $0 online-* clone and a priced original. */
function itemRichnessScore(item: OrderItem): number {
  let score = 0;
  const price = typeof item.price === 'number' ? item.price : 0;
  if (price > 0) score += 100 + price;
  if (item.selectedMeatOption) score += 10;
  if (item.notes) score += 5;
  if (item.imageUrl || item.imageUrlThumb) score += 2;
  if (item.categoryId) score += 1;
  return score;
}

/**
 * Collapse false twins created when reconcile invented `online-*` $0 clones while
 * keeping the original priced lines (paid online orders before lineIds were stamped).
 */
export function collapseOnlineSnapshotDuplicateClones(items: Order['items']): Order['items'] {
  const list = coerceOrderItemsArray(items);
  if (list.length < 2) return list;

  const dropped = new Set<number>();
  const next = list.map((item) => ({ ...item }));

  for (let i = 0; i < next.length; i++) {
    if (dropped.has(i)) continue;
    const a = next[i];
    const lineId = typeof a.lineId === 'string' ? a.lineId : '';
    if (!lineId.startsWith('online-')) continue;

    for (let j = 0; j < next.length; j++) {
      if (i === j || dropped.has(j)) continue;
      const b = next[j];
      if (normalizeItemName(a.name) !== normalizeItemName(b.name)) continue;
      if (b.lineId && b.lineId === lineId) continue;

      const aPrice = typeof a.price === 'number' ? a.price : 0;
      const bPrice = typeof b.price === 'number' ? b.price : 0;
      const aScore = itemRichnessScore(a);
      const bScore = itemRichnessScore(b);

      // Prefer the priced / richer sibling; stamp the online-* lineId onto it.
      if (bScore > aScore || (bPrice > 0 && aPrice <= 0)) {
        next[j] = {
          ...b,
          lineId: b.lineId || lineId,
          notes: b.notes ?? a.notes,
          selectedMeatOption: b.selectedMeatOption ?? a.selectedMeatOption,
        };
        dropped.add(i);
        break;
      }
      if (aScore > bScore || (aPrice > 0 && bPrice <= 0)) {
        next[i] = {
          ...a,
          notes: a.notes ?? b.notes,
          selectedMeatOption: a.selectedMeatOption ?? b.selectedMeatOption,
        };
        dropped.add(j);
        break;
      }
      // Both incomplete — keep the online-* row (matches kitchen snapshot), drop twin.
      if (!b.lineId || !String(b.lineId).startsWith('online-')) {
        dropped.add(j);
        break;
      }
    }
  }

  return next.filter((_, idx) => !dropped.has(idx));
}

function hasOnlineSnapshotDuplicateClones(items: Order['items']): boolean {
  return collapseOnlineSnapshotDuplicateClones(items).length < coerceOrderItemsArray(items).length;
}

/** Stamp snapshot lineIds onto equal-length items when names align by index. */
function stampLineIdsFromSnapshotByIndex(
  items: Order['items'],
  snapshot: NonNullable<Order['lastSentItemSnapshot']>,
): Order['items'] {
  if (!snapshotAlignsWithItemsByIndex(items, snapshot)) return items;
  return items.map((item, idx) => {
    const snapLineId = snapshot[idx]?.lineId;
    if (!snapLineId || item.lineId === snapLineId) return item;
    if (item.lineId && item.lineId !== snapLineId) return item;
    return {
      ...item,
      lineId: snapLineId,
      notes: item.notes ?? snapshot[idx]?.notes,
      selectedMeatOption: item.selectedMeatOption ?? snapshot[idx]?.selectedMeatOption,
    };
  });
}

/** True when Firestore still stores fewer lines than kitchen snapshots / batches. */
export function orderNeedsPersistedRepair(raw: Order, repaired: Order): boolean {
  const rawItems = coerceOrderItemsArray(raw.items);
  const repairedItems = coerceOrderItemsArray(repaired.items);
  if (repairedItems.length > rawItems.length) return true;
  // Collapse of online-* $0 twins back to the priced originals.
  if (repairedItems.length < rawItems.length && hasOnlineSnapshotDuplicateClones(rawItems)) {
    return true;
  }
  const snapshotLen = raw.lastSentItemSnapshot?.length ?? 0;
  if (snapshotLen > rawItems.length) return true;
  const maxBatchIdx = getMaxBatchItemIndex(raw);
  if (maxBatchIdx >= rawItems.length) return true;
  const storedTotal = raw.totalAmount ?? 0;
  const rawItemsTotal = getOrderTotalFromItems({ ...raw, items: rawItems });
  return storedTotal > rawItemsTotal + 0.05 && snapshotLen > 0;
}

/** True when an in-memory order row should be rebuilt from kitchen snapshots. */
export function orderNeedsItemReconciliation(order: Order): boolean {
  const items = coerceOrderItemsArray(order.items);
  const snapshot = order.lastSentItemSnapshot ?? [];
  if (hasOnlineSnapshotDuplicateClones(items)) return true;

  const maxBatchIdx = getMaxBatchItemIndex(order);
  if (maxBatchIdx >= items.length) return true;
  if (snapshot.length > items.length) return true;

  // Missing snapshot lineIds are NOT truncation when lengths/names align by index
  // (paid online orders historically had snapshots with online-* ids but items without).
  const itemLineIds = new Set(items.map((i) => i.lineId).filter(Boolean) as string[]);
  const missingLineIds = snapshot.some((snap) => snap.lineId && !itemLineIds.has(snap.lineId));
  if (missingLineIds && !snapshotAlignsWithItemsByIndex(items, snapshot)) {
    return true;
  }

  const storedTotal = order.totalAmount ?? 0;
  const itemsTotal = getOrderTotalFromItems({ ...order, items });
  return storedTotal > itemsTotal + 0.05 && snapshot.length > 0;
}

function lookupMenuByName(
  name: string,
  menuByName: Map<string, MenuPriceLookup>,
): MenuPriceLookup | undefined {
  const key = name.toLowerCase().trim();
  const exact = menuByName.get(key);
  if (exact) return exact;
  for (const [menuKey, menu] of menuByName) {
    if (menuKey.includes(key) || key.includes(menuKey)) return menu;
  }
  return undefined;
}

/**
 * Rebuild truncated `items[]` from `lastSentItemSnapshot` when a stale terminal write
 * persisted totals/batches but dropped running-order lines from the array.
 *
 * Also stamps missing lineIds by index for paid online orders, and collapses false
 * online-* $0 twins that an earlier reconcile may have persisted.
 */
export function reconcileOrderItemsFromSnapshots(
  order: Order,
  menuItems?: MenuPriceLookup[],
): Order['items'] {
  const existingRaw = coerceOrderItemsArray(order.items);
  const existing = collapseOnlineSnapshotDuplicateClones(existingRaw);
  const snapshot = order.lastSentItemSnapshot ?? [];
  if (!snapshot.length) return existing;

  // Equal-length online orders: stamp lineIds only — do not invent clones.
  if (snapshotAlignsWithItemsByIndex(existing, snapshot)) {
    return stampLineIdsFromSnapshotByIndex(existing, snapshot);
  }

  if (!orderNeedsItemReconciliation({ ...order, items: existing }) && existing.length >= snapshot.length) {
    return existing;
  }

  const menuByName = new Map<string, MenuPriceLookup>();
  for (const menu of menuItems ?? []) {
    const key = menu.name.toLowerCase().trim();
    if (!menuByName.has(key)) menuByName.set(key, menu);
  }

  const result: OrderItem[] = [];
  const usedLineIds = new Set<string>();
  const usedExistingIdx = new Set<number>();

  for (let snapIdx = 0; snapIdx < snapshot.length; snapIdx++) {
    const snap = snapshot[snapIdx];
    const existingIdxByLineId =
      snap.lineId != null
        ? existing.findIndex(
            (item, idx) =>
              !usedExistingIdx.has(idx) && item.lineId === snap.lineId,
          )
        : -1;
    if (existingIdxByLineId >= 0) {
      const existingItem = existing[existingIdxByLineId];
      if (existingItem.removed === true) {
        result.push(existingItem);
        if (snap.lineId) usedLineIds.add(snap.lineId);
        usedExistingIdx.add(existingIdxByLineId);
        continue;
      }
      // Never inflate qty from lastSent — after a POS cut, lastSent may intentionally
      // be the pre-cut cart for REMOVED print; Math.max resurrected the cut and made the
      // next same-line add print an empty kitchen ticket while KDS still got a batch.
      result.push({
        ...existingItem,
        notes: existingItem.notes ?? snap.notes,
        selectedMeatOption: existingItem.selectedMeatOption ?? snap.selectedMeatOption,
      });
      if (snap.lineId) usedLineIds.add(snap.lineId);
      usedExistingIdx.add(existingIdxByLineId);
      continue;
    }

    // Index / name match when items lack lineId (classic online order shape).
    let indexMatchIdx = -1;
    const candidate = existing[snapIdx];
    if (
      candidate &&
      !usedExistingIdx.has(snapIdx) &&
      (!candidate.lineId || !usedLineIds.has(candidate.lineId)) &&
      normalizeItemName(candidate.name) === normalizeItemName(snap.name)
    ) {
      indexMatchIdx = snapIdx;
    } else {
      indexMatchIdx = existing.findIndex((item, idx) => {
        if (usedExistingIdx.has(idx)) return false;
        if (item.lineId && usedLineIds.has(item.lineId)) return false;
        return normalizeItemName(item.name) === normalizeItemName(snap.name);
      });
    }

    if (indexMatchIdx >= 0) {
      const matched = existing[indexMatchIdx];
      if (matched.removed === true) {
        result.push({
          ...matched,
          lineId: matched.lineId || snap.lineId,
        });
      } else {
        result.push({
          ...matched,
          lineId: matched.lineId || snap.lineId,
          // Keep live line qty — do not Math.max with snapshot (see lineId match above).
          notes: matched.notes ?? snap.notes,
          selectedMeatOption: matched.selectedMeatOption ?? snap.selectedMeatOption,
        });
      }
      if (snap.lineId) usedLineIds.add(snap.lineId);
      usedExistingIdx.add(indexMatchIdx);
      continue;
    }

    const menu = lookupMenuByName(snap.name, menuByName);
    result.push({
      id: menu ? String(menu.id) : snap.lineId,
      name: snap.name,
      quantity: snap.quantity ?? 1,
      notes: snap.notes,
      selectedMeatOption: snap.selectedMeatOption,
      price: menu?.price ?? 0,
      lineId: snap.lineId,
      imageUrl: menu?.imageUrl,
      imageUrlThumb: menu?.imageUrlThumb,
      categoryId: menu?.categoryId,
      status: 'pending',
      served: false,
    } as OrderItem);
    if (snap.lineId) usedLineIds.add(snap.lineId);
  }

  // Keep lines the kitchen snapshot does not track (buffet package covers, mid-edit
  // adds, soft-removed rows). Skip rows already consumed via lineId or index match.
  for (let idx = 0; idx < existing.length; idx++) {
    if (usedExistingIdx.has(idx)) continue;
    const item = existing[idx];
    if (item.lineId && usedLineIds.has(item.lineId)) continue;
    result.push(item);
  }

  return collapseOnlineSnapshotDuplicateClones(result.length > 0 ? result : existing);
}

export function orderHasMeaningfulItems(order: Order): boolean {
  const items = reconcileOrderItemsFromSnapshots(order);
  if (!items.length) return false;
  return items.some((item) => {
    if (item.removed) return false;
    if ((item.quantity ?? 0) <= 0) return false;
    if (typeof item.price === 'number' && item.price < 0) return false;
    if (typeof item.id === 'string' && item.id.includes('combo-discount')) return false;
    return true;
  });
}

/**
 * Firestore occasionally stores `items` as a numeric-key map instead of an array after
 * partial updates. Coerce so we always read the full line list from the database.
 */
export function coerceOrderItemsArray(items: unknown): Order['items'] {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'object') {
    return Object.keys(items as Record<string, Order['items'][number]>)
      .filter((key) => /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => (items as Record<string, Order['items'][number]>)[key])
      .filter((item) => item != null);
  }
  return [];
}

/** Sum of meaningful line quantities (excludes removed/discount-only rows). */
export function orderMeaningfulItemQty(order: Order): number {
  const items = reconcileOrderItemsFromSnapshots(order);
  return items.reduce((sum, item) => {
    if (item.removed) return sum;
    if ((item.quantity ?? 0) <= 0) return sum;
    if (typeof item.price === 'number' && item.price < 0) return sum;
    if (typeof item.id === 'string' && item.id.includes('combo-discount')) return sum;
    return sum + (item.quantity ?? 0);
  }, 0);
}

function orderCompletenessScore(order: Order): { lines: number; qty: number; total: number } {
  const items = reconcileOrderItemsFromSnapshots(order);
  const lines = items.filter((item) => {
    if (item.removed) return false;
    if ((item.quantity ?? 0) <= 0) return false;
    if (typeof item.price === 'number' && item.price < 0) return false;
    if (typeof item.id === 'string' && item.id.includes('combo-discount')) return false;
    return true;
  }).length;
  // Use discount-aware computed total only — Math.max with stored total treated
  // discounted tickets as "more incomplete" and resurrected old amounts.
  const computedTotal = getOrderTotalFromItems({ ...order, items });
  return {
    lines,
    qty: orderMeaningfulItemQty({ ...order, items }),
    total: computedTotal,
  };
}

function pickLongerSnapshot(
  a?: Order['lastSentItemSnapshot'],
  b?: Order['lastSentItemSnapshot'],
): Order['lastSentItemSnapshot'] | undefined {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length === 0 && right.length === 0) return undefined;
  return left.length >= right.length ? a : b;
}

/** Keep the longest kitchen snapshot when merging order rows or writes. */
export function pickLongerOrderSnapshot(
  a?: Order['lastSentItemSnapshot'],
  b?: Order['lastSentItemSnapshot'],
): Order['lastSentItemSnapshot'] | undefined {
  return pickLongerSnapshot(a, b);
}

/** How long a brand-new / just-touched order stays live regardless of status (covers overnight). */
export const ORDER_LIVE_RECENT_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000;
/** Unpaid tickets older than this leave the live set (Pending still one-shot fetches within this window). */
export const ORDER_LIVE_UNPAID_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
/** Soft-deleted docs stay in the live query briefly so clients receive tombstones. */
export const ORDER_LIVE_TOMBSTONE_MAX_AGE_MS = 2 * 24 * 60 * 60 * 1000;

const OPEN_KITCHEN_LIVE_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'new',
  'preparing',
  'ready',
  'queued',
  'held',
  'paused',
  'buffet',
]);

/**
 * Single-listener membership for POS/KDS sync.
 * Must stay true for: NZ-recent tickets, open unpaid, open kitchen, short soft-delete tombstones.
 * Must become false for settled history so reconnects do not re-read weeks of paid orders.
 */
export function computeOrderLiveEligible(
  order: Pick<
    Order,
    'status' | 'paymentStatus' | 'timestamp' | 'isDeleted' | 'deletedAt' | 'pickupTime'
  > & { createdAt?: unknown },
  nowMs: number = Date.now(),
): boolean {
  if (order.isDeleted) {
    const deletedAt = typeof order.deletedAt === 'number' ? order.deletedAt : 0;
    return deletedAt > 0 && nowMs - deletedAt < ORDER_LIVE_TOMBSTONE_MAX_AGE_MS;
  }

  if (
    order.status === 'cancelled' ||
    order.status === 'refunded' ||
    order.status === 'comped'
  ) {
    return false;
  }

  const ts = orderCreatedAtMs(order as Order);
  if (ts > 0 && nowMs - ts < ORDER_LIVE_RECENT_MAX_AGE_MS) {
    return true;
  }

  if (
    order.paymentStatus === 'unpaid' &&
    ts > 0 &&
    nowMs - ts < ORDER_LIVE_UNPAID_MAX_AGE_MS
  ) {
    return true;
  }

  if (order.status && OPEN_KITCHEN_LIVE_STATUSES.has(order.status)) {
    return true;
  }

  return false;
}

/**
 * Last line of defence before Firestore setDoc: expand truncated items[],
 * preserve snapshots/batches, and align totals with stored amounts when needed.
 */
export function finalizeOrderDocumentForWrite(order: Order): Order {
  const items = coerceOrderItemsArray(order.items);
  // Soft-removes / qty cuts intentionally shorten lastSent — do not re-expand from initialKitchenSnapshot.
  const intentionalAmendment = items.some((i) => i.removed === true);
  const snapshot = intentionalAmendment
    ? (order.lastSentItemSnapshot ?? order.initialKitchenSnapshot)
    : pickLongerOrderSnapshot(order.lastSentItemSnapshot, order.initialKitchenSnapshot);
  const withSnapshot: Order = {
    ...order,
    lastSentItemSnapshot: snapshot ?? order.lastSentItemSnapshot,
    orderBatches: order.orderBatches,
  };
  const normalized = normalizeOrderRecord(withSnapshot);
  return {
    ...normalized,
    liveEligible: computeOrderLiveEligible(normalized),
  };
}

export class OrderWriteIntegrityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OrderWriteIntegrityError';
  }
}

function itemsDroppedWithoutRemoval(before: Order['items'], after: Order['items']): boolean {
  const beforeItems = coerceOrderItemsArray(before);
  const afterItems = coerceOrderItemsArray(after);
  if (afterItems.length >= beforeItems.length) return false;

  const afterLineIds = new Set(
    afterItems.map((item) => item.lineId).filter(Boolean) as string[],
  );
  for (const item of beforeItems) {
    if (!item.lineId || afterLineIds.has(item.lineId)) continue;
    if (!item.removed) return true;
  }
  return false;
}

function isLiveKitchenLine(item: OrderItem | undefined): boolean {
  if (!item || item.removed === true) return false;
  if (item.isBuffetPackage === true || item.isMealPlanSale === true) return false;
  if ((item.quantity ?? 0) <= 0) return false;
  if (typeof item.price === 'number' && item.price < 0) return false;
  if (typeof item.id === 'string' && item.id.includes('combo-discount')) return false;
  return true;
}

/**
 * True when a shorter lastSent drops a dish that is still an active kitchen line
 * on the server. Buffet covers, meal-plan sales, discounts, removals, and orphan
 * snapshot rows are not live kitchen lines.
 */
export function lastSentShrinkDropsLiveKitchenLine(serverDoc: Order, nextDoc: Order): boolean {
  const before = serverDoc.lastSentItemSnapshot ?? [];
  const afterIds = new Set(
    (nextDoc.lastSentItemSnapshot ?? []).map((row) => row.lineId).filter(Boolean) as string[],
  );
  const serverByLine = new Map(
    coerceOrderItemsArray(serverDoc.items)
      .filter((item) => item.lineId)
      .map((item) => [item.lineId as string, item]),
  );
  for (const row of before) {
    if (!row.lineId || afterIds.has(row.lineId)) continue;
    if (isLiveKitchenLine(serverByLine.get(row.lineId))) return true;
  }
  return false;
}

/**
 * Running-order adds rebuild lastSent from the pre-update cart. That list omits
 * buffet / discount / removed rows an older snapshot may still contain. Keep any
 * omitted row that is still a live kitchen line so the baseline does not lose a dish.
 */
export function mergeLastSentSnapshotForRunningAdd(
  serverSnap: Order['lastSentItemSnapshot'],
  proposedSnap: Order['lastSentItemSnapshot'],
  serverItems: Order['items'],
): NonNullable<Order['lastSentItemSnapshot']> {
  const proposed = proposedSnap ?? [];
  const server = serverSnap ?? [];
  if (proposed.length >= server.length) return proposed;
  const proposedIds = new Set(proposed.map((row) => row.lineId).filter(Boolean) as string[]);
  const serverByLine = new Map(
    coerceOrderItemsArray(serverItems)
      .filter((item) => item.lineId)
      .map((item) => [item.lineId as string, item]),
  );
  const retained = server.filter((row) => {
    if (!row.lineId || proposedIds.has(row.lineId)) return false;
    return isLiveKitchenLine(serverByLine.get(row.lineId));
  });
  return retained.length ? [...proposed, ...retained] : proposed;
}

/**
 * Throws when a proposed order write would shrink items[] or kitchen snapshots
 * without an explicit removal — blocks stale offline cache from overwriting server truth.
 */
export function assertOrderWriteIntegrity(serverDoc: Order | undefined, nextDoc: Order): void {
  if (!serverDoc) return;

  const beforeItems = coerceOrderItemsArray(serverDoc.items);
  const afterItems = coerceOrderItemsArray(nextDoc.items);

  if (itemsDroppedWithoutRemoval(serverDoc.items, nextDoc.items)) {
    throw new OrderWriteIntegrityError(
      `Blocked order write: items shrank (${beforeItems.length} -> ${afterItems.length}) without explicit removals`,
    );
  }

  const intentionalAmendment = isIntentionalOrderAmendment(serverDoc, nextDoc);

  const beforeSnapLen = serverDoc.lastSentItemSnapshot?.length ?? 0;
  const afterSnapLen = nextDoc.lastSentItemSnapshot?.length ?? 0;
  // Additions rebuild lastSent from the pre-update cart, which is shorter when the
  // stored snapshot still lists a buffet cover, discount, removal, or orphan row.
  // Block only when that shorter snapshot drops a dish still live on the server.
  if (
    afterSnapLen < beforeSnapLen &&
    !intentionalAmendment &&
    lastSentShrinkDropsLiveKitchenLine(serverDoc, nextDoc)
  ) {
    throw new OrderWriteIntegrityError(
      `Blocked order write: lastSentItemSnapshot shrank (${beforeSnapLen} -> ${afterSnapLen})`,
    );
  }

  const beforeInitialLen = serverDoc.initialKitchenSnapshot?.length ?? 0;
  const afterInitialLen = nextDoc.initialKitchenSnapshot?.length ?? 0;
  if (afterInitialLen < beforeInitialLen) {
    throw new OrderWriteIntegrityError(
      `Blocked order write: initialKitchenSnapshot shrank (${beforeInitialLen} -> ${afterInitialLen})`,
    );
  }

  const maxBatchIdx = getMaxBatchItemIndex(nextDoc);
  const snapLen = Math.max(afterSnapLen, beforeSnapLen);
  if (maxBatchIdx >= afterItems.length && snapLen > afterItems.length) {
    throw new OrderWriteIntegrityError(
      `Blocked order write: batches reference index ${maxBatchIdx} but items has ${afterItems.length} lines`,
    );
  }

  const storedTotal = nextDoc.totalAmount ?? 0;
  const itemsTotal = getOrderTotalFromItems({ ...nextDoc, items: afterItems });
  if (storedTotal > itemsTotal + 0.05 && snapLen > afterItems.length) {
    throw new OrderWriteIntegrityError(
      `Blocked order write: totalAmount ${storedTotal} exceeds items total ${itemsTotal.toFixed(2)} with incomplete lines`,
    );
  }
}

function mergeItemFieldsPreferComplete(base: OrderItem[], preferred: OrderItem[]): OrderItem[] {
  const byLineId = new Map<string, OrderItem>();
  for (const item of base) {
    if (item.lineId) byLineId.set(item.lineId, item);
  }
  const preferredLineIds = new Set(
    preferred.map((item) => item.lineId).filter(Boolean) as string[],
  );
  const mergedPreferred = preferred.map((item) => {
    const baseItem = item.lineId ? byLineId.get(item.lineId) : undefined;
    if (!baseItem) return item;
    // Soft-delete / intentional qty cut: never resurrect via Math.max
    if (item.removed === true || baseItem.removed === true) {
      const source = item.removed === true ? item : baseItem;
      return {
        ...baseItem,
        ...item,
        removed: true,
        quantity: source.quantity,
        price:
          typeof source.price === 'number' && source.price > 0
            ? source.price
            : (baseItem.price ?? item.price),
        notes: item.notes ?? baseItem.notes,
        selectedMeatOption: item.selectedMeatOption ?? baseItem.selectedMeatOption,
      };
    }
    const mergedQty = Number(item.quantity ?? 0);
    let qtyCompleted = Math.max(
      Number(baseItem.quantityCompleted ?? 0),
      Number(item.quantityCompleted ?? 0),
    );
    // Legacy completed lines with no quantityCompleted: only the pre-merge qty was done.
    // Do NOT treat sticky status==='completed' as completing newly added units.
    if (
      qtyCompleted <= 0 &&
      (baseItem.status === 'completed' || item.status === 'completed')
    ) {
      const priorQty = Number(baseItem.quantity ?? 0);
      if (mergedQty > priorQty && priorQty > 0) {
        qtyCompleted = priorQty;
      } else {
        qtyCompleted = mergedQty;
      }
    }
    const qtyServed = Math.max(
      Number(baseItem.quantityServed ?? 0),
      Number(item.quantityServed ?? 0),
    );
    const completedByBatch = mergeQuantityByBatchMaps(
      baseItem.quantityCompletedByBatch,
      item.quantityCompletedByBatch,
    );
    const servedByBatch = mergeQuantityByBatchMaps(
      baseItem.quantityServedByBatch,
      item.quantityServedByBatch,
    );
    const kitchenDone = mergedQty > 0 && qtyCompleted + 0.001 >= mergedQty;
    return {
      ...baseItem,
      ...item,
      price: (typeof item.price === 'number' && item.price > 0) ? item.price : (baseItem.price ?? item.price),
      // Prefer the chosen side's quantity — Math.max blocked intentional qty cuts on Update.
      quantity: item.quantity ?? 0,
      notes: item.notes ?? baseItem.notes,
      selectedMeatOption: item.selectedMeatOption ?? baseItem.selectedMeatOption,
      // KDS/Service progress must survive "prefer local cart" merges after Update.
      quantityCompleted: qtyCompleted > 0 ? qtyCompleted : (item.quantityCompleted ?? baseItem.quantityCompleted),
      quantityServed: qtyServed > 0 ? qtyServed : (item.quantityServed ?? baseItem.quantityServed),
      ...(completedByBatch ? { quantityCompletedByBatch: completedByBatch } : {}),
      ...(servedByBatch ? { quantityServedByBatch: servedByBatch } : {}),
      served: !!(baseItem.served || item.served || qtyServed > 0),
      status: kitchenDone ? 'completed' : 'pending',
    };
  });

  // Keep brand-new lines that exist only on the other side (running-order adds on live).
  const extras = base.filter((item) => {
    if (item.removed) return false;
    if (item.lineId) return !preferredLineIds.has(item.lineId);
    return !preferred.some(
      (p) =>
        p.id === item.id &&
        (p.selectedMeatOption ?? '') === (item.selectedMeatOption ?? '') &&
        !p.removed,
    );
  });
  return extras.length > 0 ? [...mergedPreferred, ...extras] : mergedPreferred;
}

function mergeQuantityByBatchMaps(
  a?: Record<string, number>,
  b?: Record<string, number>,
): Record<string, number> | undefined {
  if (!a && !b) return undefined;
  const out: Record<string, number> = { ...(a ?? {}) };
  for (const [key, value] of Object.entries(b ?? {})) {
    out[key] = Math.max(Number(out[key] ?? 0), Number(value ?? 0));
  }
  return out;
}

/** True when incoming marks lines `removed` that were still active on prev (soft-delete amendment). */
export function incomingHasSoftRemovals(prev: Order, incoming: Order): boolean {
  const prevItems = coerceOrderItemsArray(prev.items);
  const incomingItems = coerceOrderItemsArray(incoming.items);
  const prevByLineId = new Map(
    prevItems.filter((i) => i.lineId).map((i) => [i.lineId as string, i]),
  );
  for (const item of incomingItems) {
    if (!item.lineId || item.removed !== true) continue;
    const prevItem = prevByLineId.get(item.lineId);
    if (prevItem && prevItem.removed !== true) return true;
  }
  return false;
}

function incomingReflectsExplicitRemovals(prev: Order, incoming: Order): boolean {
  if (incomingHasSoftRemovals(prev, incoming)) return true;

  const prevItems = coerceOrderItemsArray(prev.items);
  const incomingItems = coerceOrderItemsArray(incoming.items);
  if (incomingItems.length >= prevItems.length) return false;

  const incomingLineIds = new Set(
    incomingItems.map((item) => item.lineId).filter(Boolean) as string[],
  );
  for (const item of prevItems) {
    if (!item.lineId || incomingLineIds.has(item.lineId)) continue;
    if (!item.removed) return false;
  }
  return true;
}

/** Soft-remove or meaningful qty decrease — allow shorter kitchen snapshots on write. */
export function isIntentionalOrderAmendment(before: Order, after: Order): boolean {
  if (incomingHasSoftRemovals(before, after)) return true;
  if (incomingReflectsExplicitRemovals(before, after)) return true;
  return orderMeaningfulItemQty(after) + 0.001 < orderMeaningfulItemQty(before);
}

function orderLifecycleRank(order: Order): number {
  let rank = 0;
  switch (order.status) {
    case 'served':
      rank = 50;
      break;
    case 'ready':
      rank = 40;
      break;
    case 'preparing':
      rank = 30;
      break;
    case 'new':
    case 'queued':
      rank = 20;
      break;
    case 'cancelled':
    case 'refunded':
    case 'comped':
      // Terminal — must not lose to preparing/ready via rank comparison.
      rank = 100;
      break;
    default:
      rank = 10;
  }
  if (order.deliveryInfo?.uberReadySentAt) rank += 5;
  if (order.servedAt) rank += 2;
  if (order.readyAt) rank += 1;
  return rank;
}

export function isTerminalOrderStatus(status: Order['status'] | undefined): boolean {
  return status === 'cancelled' || status === 'refunded' || status === 'comped';
}

/** Prefer newer Ready / Picked Up lifecycle fields when item completeness is tied. */
export function mergeOrderLifecyclePreferNewer(base: Order, other: Order): Order {
  // Never resurrect an active kitchen status over an explicit cancel/refund/comp.
  if (isTerminalOrderStatus(base.status) && !isTerminalOrderStatus(other.status)) {
    return base;
  }
  if (isTerminalOrderStatus(other.status) && !isTerminalOrderStatus(base.status)) {
    return {
      ...base,
      status: other.status,
      readyAt: other.readyAt ?? base.readyAt,
      servedAt: other.servedAt ?? base.servedAt,
      deliveryInfo: {
        ...base.deliveryInfo,
        ...other.deliveryInfo,
        uberReadySentAt:
          other.deliveryInfo?.uberReadySentAt ?? base.deliveryInfo?.uberReadySentAt,
      },
    };
  }
  if (orderLifecycleRank(other) <= orderLifecycleRank(base)) return base;
  return {
    ...base,
    status: other.status ?? base.status,
    readyAt: other.readyAt ?? base.readyAt,
    servedAt: other.servedAt ?? base.servedAt,
    deliveryInfo: {
      ...base.deliveryInfo,
      ...other.deliveryInfo,
      uberReadySentAt:
        other.deliveryInfo?.uberReadySentAt ?? base.deliveryInfo?.uberReadySentAt,
    },
  };
}

/** True when `a` has strictly more lines / qty than `b` (or non-zero vs empty total). */
export function isOrderMoreCompleteThan(a: Order, b: Order): boolean {
  const scoreA = orderCompletenessScore(a);
  const scoreB = orderCompletenessScore(b);
  if (scoreA.lines > scoreB.lines) return true;
  if (scoreA.lines < scoreB.lines) return false;
  if (scoreA.qty > scoreB.qty + 0.001) return true;
  if (scoreA.qty + 0.001 < scoreB.qty) return false;
  // Same lines/qty: do NOT prefer higher total — discounts legitimately lower totals.
  // Only treat the other side as incomplete when its money looks empty/corrupt.
  if (scoreB.total < 0.01 && scoreA.total > 0.01) return true;
  return false;
}

function pickMoreCompleteItems(prev: Order, incoming: Order): Order['items'] {
  const prevReconciled = reconcileOrderItemsFromSnapshots(prev);
  const incomingReconciled = reconcileOrderItemsFromSnapshots(incoming);

  // Soft-remove or intentional qty cut: keep the amending side's quantities.
  if (isIntentionalOrderAmendment(prev, incoming)) {
    return mergeItemFieldsPreferComplete(prevReconciled, incomingReconciled);
  }

  if (incomingReflectsExplicitRemovals(prev, incoming)) {
    return mergeItemFieldsPreferComplete(prevReconciled, incomingReconciled);
  }

  if (isOrderMoreCompleteThan(prev, incoming)) {
    return mergeItemFieldsPreferComplete(incomingReconciled, prevReconciled);
  }
  if (isOrderMoreCompleteThan(incoming, prev)) {
    return mergeItemFieldsPreferComplete(prevReconciled, incomingReconciled);
  }

  const preferred = incomingReconciled.length >= prevReconciled.length ? incomingReconciled : prevReconciled;
  const fallback = preferred === incomingReconciled ? prevReconciled : incomingReconciled;
  return mergeItemFieldsPreferComplete(fallback, preferred);
}

function mergeOrderLineItemsPreferComplete(prev: Order, incoming: Order): Order {
  const intentional =
    isIntentionalOrderAmendment(prev, incoming) ||
    incomingHasSoftRemovals(prev, incoming) ||
    incomingReflectsExplicitRemovals(prev, incoming);
  // On intentional amendments, accept the shorter kitchen snapshot from incoming when provided.
  const snapshot = intentional
    ? (incoming.lastSentItemSnapshot ?? prev.lastSentItemSnapshot)
    : (() => {
        const itemsGrew =
          orderMeaningfulItemQty(incoming) > orderMeaningfulItemQty(prev) + 0.001 ||
          coerceOrderItemsArray(incoming.items).filter((i) => !i.removed).length >
            coerceOrderItemsArray(prev.items).filter((i) => !i.removed).length;
        // Prefer the post-write baseline (usually shorter / pre-addition) so kitchen print can diff.
        if (itemsGrew && incoming.lastSentItemSnapshot) {
          return incoming.lastSentItemSnapshot;
        }
        const prevSnap = prev.lastSentItemSnapshot;
        const incomingSnap = incoming.lastSentItemSnapshot;
        const activeLineIds = new Set(
          coerceOrderItemsArray(incoming.items ?? prev.items)
            .filter((i) => !i.removed)
            .map((i) => i.lineId)
            .filter((id): id is string => !!id),
        );
        const coversAllCurrent = (snap?: Order['lastSentItemSnapshot']) => {
          if (!snap?.length || activeLineIds.size === 0) return false;
          if (snap.length < activeLineIds.size) return false;
          return [...activeLineIds].every((id) => snap.some((s) => s.lineId === id));
        };
        // Never let a "full cart" snapshot win over a real pre-update baseline — that
        // makes Print Order show "No items to show" after a successful Update.
        if (coversAllCurrent(incomingSnap) && prevSnap && !coversAllCurrent(prevSnap)) {
          return prevSnap;
        }
        if (coversAllCurrent(prevSnap) && incomingSnap && !coversAllCurrent(incomingSnap)) {
          return incomingSnap;
        }
        return pickLongerSnapshot(prevSnap, incomingSnap);
      })();
  const initialSnapshot = intentional
    ? (incoming.initialKitchenSnapshot ?? prev.initialKitchenSnapshot)
    : pickLongerSnapshot(prev.initialKitchenSnapshot, incoming.initialKitchenSnapshot);
  const merged: Order = {
    ...incoming,
    items: pickMoreCompleteItems(prev, incoming),
    payments:
      (incoming.payments?.length ?? 0) >= (prev.payments?.length ?? 0)
        ? (incoming.payments ?? prev.payments)
        : prev.payments,
    paymentStatus: incoming.paymentStatus ?? prev.paymentStatus,
    subBills: incoming.subBills ?? prev.subBills,
    lastSentItemSnapshot: snapshot ?? prev.lastSentItemSnapshot ?? incoming.lastSentItemSnapshot,
    initialKitchenSnapshot: initialSnapshot ?? prev.initialKitchenSnapshot ?? incoming.initialKitchenSnapshot,
    orderBatches:
      (incoming.orderBatches?.length ?? 0) >= (prev.orderBatches?.length ?? 0)
        ? (incoming.orderBatches ?? prev.orderBatches)
        : prev.orderBatches,
    status: incoming.status ?? prev.status,
    // Seed from incoming; always recompute below when discount/amendment — avoid Math.max
    // resurrecting pre-discount totals.
    subtotal: incoming.subtotal ?? prev.subtotal,
    taxAmount: incoming.taxAmount ?? prev.taxAmount,
    totalAmount: incoming.totalAmount ?? prev.totalAmount,
  };
  // Soft-remove / qty cut / discount / normal merge: money follows items + discount fields
  {
    const totals = getOrderTotals({ ...merged, items: merged.items });
    // If incoming looked truncated (empty money) keep prev totals until items are repaired.
    const incomingMoney = getOrderTotalFromItems(incoming);
    const prevMoney = getOrderTotalFromItems(prev);
    if (intentional || orderHasAppliedDiscount(incoming) || incomingMoney >= 0.01 || prevMoney < 0.01) {
      merged.subtotal = totals.subtotal;
      merged.taxAmount = totals.taxAmount;
      merged.totalAmount = totals.totalAmount;
    } else if (!intentional && prevMoney > incomingMoney + 0.05) {
      // Truncation repair: prefer prev money only when incoming items look empty/corrupt
      merged.subtotal = prev.subtotal ?? totals.subtotal;
      merged.taxAmount = prev.taxAmount ?? totals.taxAmount;
      merged.totalAmount = prev.totalAmount ?? totals.totalAmount;
    } else {
      merged.subtotal = totals.subtotal;
      merged.taxAmount = totals.taxAmount;
      merged.totalAmount = totals.totalAmount;
    }
  }
  return normalizeOrderRecord(merged);
}

/**
 * Parse a Firestore order row: reconcile truncated items[], then align totals with the full line list.
 */
export function normalizeOrderRecord(order: Order, menuItems?: MenuPriceLookup[]): Order {
  let items = reconcileOrderItemsFromSnapshots(order, menuItems);
  items = coerceOrderItemsArray(items);

  let { subtotal, taxAmount, totalAmount } = getOrderTotals({ ...order, items });
  const storedTotal = order.totalAmount ?? 0;
  const computedTotal = totalAmount;

  // Only keep a higher stored total when items look truncated — never when a discount
  // legitimately lowered the bill (same lines, lower computed total).
  if (
    storedTotal > computedTotal + 0.05 &&
    !orderHasAppliedDiscount({ ...order, items }) &&
    orderNeedsItemReconciliation({ ...order, items: coerceOrderItemsArray(order.items) })
  ) {
    totalAmount = storedTotal;
    taxAmount = typeof order.taxAmount === 'number' ? order.taxAmount : taxAmount;
    subtotal = typeof order.subtotal === 'number' ? order.subtotal : subtotal;
  }

  return {
    ...order,
    items,
    subtotal,
    taxAmount,
    totalAmount,
  };
}

/** Fetch the authoritative server row when opening a ticket (bypasses IndexedDB cache). */
export async function fetchOrderFromServer(
  orderId: string,
  fallback: Order,
  getById: (id: string, options?: { preferServer?: boolean }) => Promise<Order | null>,
  menuItems?: MenuPriceLookup[],
  persistRepair?: (repaired: Order) => Promise<void>,
): Promise<Order> {
  try {
    const remote = await getById(orderId, { preferServer: true });
    if (!remote) return normalizeOrderRecord(fallback, menuItems);
    const repaired = normalizeOrderRecord(remote, menuItems);
    if (persistRepair && orderNeedsItemReconciliation(remote)) {
      await persistRepair(repaired);
    }
    return repaired;
  } catch (err) {
    console.warn(`fetchOrderFromServer(${orderId}) failed, using local snapshot`, err);
    return normalizeOrderRecord(fallback, menuItems);
  }
}

/**
 * Prefer a complete local/previous order over an incomplete live snapshot.
 * Firestore reconnects and partial cache hits can briefly deliver docs with empty
 * `items` (or a near-zero total). Overwriting good state causes payment-modal flicker,
 * "Order not found" races, KDS tickets vanishing, and Pending cards reverting to an
 * earlier running-order batch after multi-terminal or offline sync.
 */
export function mergeOrderPreferComplete(prev: Order | undefined, incoming: Order): Order {
  if (!prev || prev.id !== incoming.id) return normalizeOrderRecord(incoming);
  if (incoming.isDeleted) return incoming;

  // Intentional soft-deletes / qty cuts / line removals: prefer the amending payload.
  if (isIntentionalOrderAmendment(prev, incoming) || incomingReflectsExplicitRemovals(prev, incoming)) {
    return mergeOrderLineItemsPreferComplete(prev, incoming);
  }

  // Stale fuller snapshot must not overwrite a local soft-remove amendment.
  if (incomingHasSoftRemovals(incoming, prev)) {
    return mergeOrderLifecyclePreferNewer(
      mergeOrderLineItemsPreferComplete(incoming, prev),
      incoming,
    );
  }

  // Local qty-cut vs a stale higher-qty snapshot on the *same* lines — keep the cut.
  // Do NOT use this when `incoming` has more active lines (that is a running-order addition);
  // otherwise Update with new items is discarded and kitchen never sees the delta batch.
  // Same-line qty increases also look like "prev is a cut of incoming" — only prefer the
  // lower prev when incoming still looks like a stale full cart (items ≈ lastSent), not when
  // items grew above the kitchen baseline (running-order qty add).
  if (isIntentionalOrderAmendment(incoming, prev)) {
    const incomingActive = coerceOrderItemsArray(incoming.items).filter((i) => !i.removed).length;
    const prevActive = coerceOrderItemsArray(prev.items).filter((i) => !i.removed).length;
    if (incomingActive <= prevActive) {
      const incomingQty = orderMeaningfulItemQty(incoming);
      const prevQty = orderMeaningfulItemQty(prev);
      const incomingSnapQty = (incoming.lastSentItemSnapshot ?? []).reduce(
        (sum, row) => sum + (Number(row.quantity) || 0),
        0,
      );
      const incomingLooksLikeStaleFullCart =
        incomingQty <= prevQty + 0.001 ||
        incomingSnapQty + 0.001 >= incomingQty;
      if (incomingLooksLikeStaleFullCart) {
        return mergeOrderLifecyclePreferNewer(
          mergeOrderLineItemsPreferComplete(incoming, prev),
          incoming,
        );
      }
      // else: incoming cart grew above its lastSent baseline — keep the qty add (fall through)
    }
  }

  const prevRawItems = coerceOrderItemsArray(prev.items).length;
  const incomingRawItems = coerceOrderItemsArray(incoming.items).length;
  const prevSnapshotLen = prev.lastSentItemSnapshot?.length ?? 0;
  const incomingSnapshotLen = incoming.lastSentItemSnapshot?.length ?? 0;

  const incomingLooksTruncated =
    prevSnapshotLen > incomingSnapshotLen ||
    prevRawItems > incomingRawItems ||
    isOrderMoreCompleteThan(prev, incoming) ||
    orderNeedsItemReconciliation(incoming);

  const prevHasItems = orderHasMeaningfulItems(prev);
  const incomingHasItems = orderHasMeaningfulItems(incoming);
  const incomingZeroPriced =
    prevHasItems &&
    incomingHasItems &&
    getOrderTotalFromItems(prev) > 0.01 &&
    getOrderTotalFromItems(incoming) < 0.01;

  // Only merge when the live/incoming row looks incomplete — never invent a hybrid
  // across unrelated table contexts. Always keep incoming tableId/tableName/tableNumber.
  if (incomingLooksTruncated || (prevHasItems && !incomingHasItems) || incomingZeroPriced) {
    const merged = mergeOrderLineItemsPreferComplete(prev, incoming);
    return mergeOrderLifecyclePreferNewer(
      {
        ...merged,
        tableId: incoming.tableId ?? merged.tableId,
        tableNumber: incoming.tableNumber ?? merged.tableNumber,
        tableName: incoming.tableName ?? merged.tableName,
      },
      incoming,
    );
  }

  return normalizeOrderRecord(mergeOrderLifecyclePreferNewer(incoming, prev));
}

/** Cheap fingerprint so React can skip setState when a listener snapshot is unchanged. */
export function ordersListSignature(orders: Order[]): string {
  return orders
    .map((o) => {
      const pay = o.payments?.length ?? 0;
      const items = coerceOrderItemsArray(o.items);
      const lines = items.length;
      const qty = orderMeaningfulItemQty(o);
      const total = (getOrderTotalFromItems(o) ?? o.totalAmount ?? 0).toFixed(2);
      const kitchenKey = items
        .map(
          (i) =>
            `${i.lineId ?? i.id}:${i.quantity}:${i.quantityCompleted ?? 0}:${i.status ?? ''}:${i.removed ? 1 : 0}`,
        )
        .join(',');
      const batches = o.orderBatches?.length ?? 0;
      return `${o.id}:${o.paymentStatus ?? ''}:${o.status ?? ''}:${pay}:${lines}:${qty}:${total}:${o.tableId ?? ''}:${batches}:${kitchenKey}`;
    })
    .sort()
    .join('|');
}

/** Merge a list of order snapshots, keeping the most complete row per id. */
export function mergeOrdersByIdPreferComplete(orders: Order[]): Order[] {
  const map = new Map<string, Order>();
  for (const order of orders) {
    if (order.isDeleted) {
      map.delete(order.id);
      continue;
    }
    map.set(order.id, normalizeOrderRecord(mergeOrderPreferComplete(map.get(order.id), order)));
  }
  return Array.from(map.values());
}

const FLOOR_PLAN_DINE_IN_STATUSES: OrderStatus[] = [
  'new',
  'preparing',
  'ready',
  'served',
  'paused',
  'held',
  'queued',
];

/**
 * True when a buffet cover is still in session (on floor / unpaid rail).
 * Preferred status is `ready` (package needs no kitchen); legacy docs used `buffet`.
 */
export function isActiveBuffetSession(order: Order): boolean {
  if (order.orderType !== 'buffet' || order.isDeleted) return false;
  if (
    order.status === 'served' ||
    order.status === 'cancelled' ||
    order.status === 'refunded' ||
    order.status === 'comped'
  ) {
    return false;
  }
  return order.status === 'ready' || order.status === 'buffet' || order.status === 'new' || order.status === 'preparing';
}

/** Buffet package cover line (no kitchen) vs drinks/desserts/add-ons. */
export function isBuffetPackageLine(item: Pick<Order['items'][number], 'isBuffetPackage' | 'price' | 'id'>): boolean {
  return item.isBuffetPackage === true;
}

/**
 * True when cart lines include buffet extras (or qty/notes changes) not yet on the placed order.
 * Used by POS OrderSummary to show Send extras / auto-open pay rules.
 */
export function hasPendingBuffetExtras(
  orderItems: Order['items'],
  currentOrder: Pick<Order, 'orderType' | 'items'> | null | undefined,
): boolean {
  if (!currentOrder || currentOrder.orderType !== 'buffet') return false;
  return orderItems.some((item) => {
    if (item.removed || item.isBuffetPackage || item.price < 0) return false;
    const existing = currentOrder.items.find(
      (oi) =>
        !oi.removed &&
        ((item.lineId && oi.lineId === item.lineId) ||
          (oi.id === item.id && oi.selectedMeatOption === item.selectedMeatOption)),
    );
    if (!existing) return true;
    if (existing.quantity !== item.quantity) return true;
    if ((existing.notes || '') !== (item.notes || '')) return true;
    return false;
  });
}

/** Non-package lines on a buffet order that kitchen/service should prepare. */
export function getBuffetKitchenItems(order: Order): Order['items'] {
  return (order.items ?? []).filter((item) => {
    if (item.removed) return false;
    if (isBuffetPackageLine(item)) return false;
    const isDiscount = item.price < 0 || String(item.id).includes('combo-discount');
    return !isDiscount;
  });
}

/** True when an order is tied to a floor-plan table (numeric id or legacy tableNumber). */
export function orderMatchesFloorPlanTable(order: Order, tableId: number): boolean {
  const tableIdNum = Number(tableId);
  if (!Number.isFinite(tableIdNum)) return false;

  // Prefer authoritative tableId. Matching tableId OR tableNumber caused one order to
  // appear on two tables when those fields diverged (transfer / partial stale writes).
  if (order.tableId != null && Number.isFinite(Number(order.tableId))) {
    return Number(order.tableId) === tableIdNum;
  }
  return order.tableNumber != null && Number(order.tableNumber) === tableIdNum;
}

function isOrderOnBusinessDay(order: Order, businessTimeZone: string): boolean {
  const placedMs = orderCreatedAtMs(order);
  if (!Number.isFinite(placedMs) || placedMs <= 0) return false;
  const orderDayKey = new Date(placedMs).toLocaleDateString('en-CA', { timeZone: businessTimeZone });
  const todayKey = ymdInTimeZone(businessTimeZone);
  return orderDayKey === todayKey;
}

/**
 * True when an order should mark a table occupied on the floor plan.
 * Open unpaid dine-in tabs stay occupied until paid (may cross midnight).
 * Buffet sessions stay occupied until ended (served), whether paid or not.
 */
export function isFloorPlanActiveOrder(
  order: Order,
  options?: { businessTimeZone?: string; nowMs?: number },
): boolean {
  if (order.isDeleted) return false;
  if (order.status === 'cancelled' || order.status === 'refunded' || order.status === 'comped') return false;
  if (!orderHasMeaningfulItems(order)) return false;

  const businessTimeZone = options?.businessTimeZone || 'Pacific/Auckland';
  const nowMs = options?.nowMs ?? Date.now();
  const todayKey = new Date(nowMs).toLocaleDateString('en-CA', { timeZone: businessTimeZone });

  if (order.orderType === 'buffet') {
    // Stay on the floor for the whole session (paid or not) until ended → served.
    return isActiveBuffetSession(order);
  }

  const isDineIn =
    order.orderType === 'dine-in' ||
    (order.tableId != null &&
      order.orderType !== 'takeaway' &&
      order.orderType !== 'online' &&
      order.orderType !== 'delivery' &&
      order.orderType !== 'uber-doordash' &&
      order.orderType !== 'event');

  if (!isDineIn) return false;
  if (order.paymentStatus === 'paid') return false;

  if (order.status === 'served') {
    // Prefer servedAt calendar day; fall back to order placed day when servedAt is missing.
    const servedMs = toMillis(order.servedAt);
    const dayMs = servedMs > 0 ? servedMs : orderCreatedAtMs(order);
    if (!Number.isFinite(dayMs) || dayMs <= 0) return false;
    const dayKey = new Date(dayMs).toLocaleDateString('en-CA', { timeZone: businessTimeZone });
    return dayKey === todayKey;
  }

  return FLOOR_PLAN_DINE_IN_STATUSES.includes(order.status);
}

/** Floor-plan occupancy for a specific table (visual + table status sync). */
export function isFloorPlanActiveTableOrder(
  order: Order,
  tableId: number,
  businessTimeZone?: string,
): boolean {
  if (!orderMatchesFloorPlanTable(order, tableId)) return false;
  return isFloorPlanActiveOrder(order, { businessTimeZone });
}

function orderTimestampMs(order: Order): number {
  const ts = order.timestamp;
  if (typeof ts === 'number') return ts;
  if (ts instanceof Date) return ts.getTime();
  if (ts && typeof (ts as { seconds?: number }).seconds === 'number') {
    return (ts as { seconds: number }).seconds * 1000;
  }
  return 0;
}

function isTodayInNz(timestamp: number): boolean {
  const timeZone = 'Pacific/Auckland';
  const orderDateStr = new Date(timestamp).toLocaleDateString('en-CA', { timeZone });
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone });
  return orderDateStr === todayStr;
}

/** NZ calendar day for placed time or (online) scheduled pickup — matches POS Pending. */
export function isKitchenRelevantCalendarDay(order: Order): boolean {
  const placedMs = orderCreatedAtMs(order);
  if (placedMs > 0 && isTodayInNz(placedMs)) return true;
  if (order.orderType === 'online') {
    const pickupMs = toMillis(order.pickupTime);
    if (pickupMs > 0 && isTodayInNz(pickupMs)) return true;
  }
  return false;
}

/** True when an order should appear on kitchen boards (not terminal / served). */
export function isOpenKitchenTicketStatus(order: Order): boolean {
  if (
    order.status === 'cancelled' ||
    order.status === 'refunded' ||
    order.status === 'comped' ||
    order.status === 'served'
  ) {
    return false;
  }
  return order.status === 'new' || order.status === 'preparing' || order.status === 'ready';
}

/**
 * Paid online tickets whose pickup window passed long ago with no kitchen progress.
 * Hides abandoned test / never-prepped orders from KDS without affecting POS Pending.
 */
export function isStaleOnlineKitchenTicket(order: Order, nowMs: number = Date.now()): boolean {
  if (order.orderType !== 'online' || order.paymentStatus !== 'paid') return false;
  if (order.status !== 'new' && order.status !== 'preparing') return false;
  const pickupMs = toMillis(order.pickupTime);
  if (pickupMs <= 0) return false;
  const hasKitchenProgress =
    order.status === 'preparing' ||
    order.preparingStartedAt != null ||
    (order.items ?? []).some((item) => (item.quantityCompleted ?? 0) > 0);
  if (hasKitchenProgress) return false;
  const staleGraceMs = 2 * 60 * 60 * 1000;
  return nowMs > pickupMs + staleGraceMs;
}

/** Pickup / platform orders that leave Pending once staff confirms handoff. */
function isHandedOffPickupOrder(order: Order): boolean {
  return (
    order.status === 'served' &&
    (order.orderType === 'uber-doordash' ||
      order.orderType === 'takeaway' ||
      order.orderType === 'online' ||
      order.orderType === 'delivery' ||
      order.orderType === 'event')
  );
}

/**
 * True when an order belongs in the POS Pending queue.
 * Uber/DoorDash orders stay unpaid in POS (paid via platform) but must drop off Pending once served.
 */
export function isPendingPosOrder(order: Order): boolean {
  if (order.status === 'cancelled' || order.status === 'refunded' || order.status === 'comped') return false;
  if (order.isDeleted) return false;
  if (!orderHasMeaningfulItems(order)) return false;
  if (isHandedOffPickupOrder(order)) return false;

  if (order.paymentStatus === 'unpaid') {
    if (isUnpaidOnlineWebOrder(order)) return false;
    return true;
  }

  const orderTs = orderTimestampMs(order);

  if (order.orderType === 'takeaway' && order.status !== 'served') {
    return isTodayInNz(orderTs);
  }

  if (order.orderType === 'online' && order.paymentStatus === 'paid' && order.status !== 'served') {
    return isTodayInNz(orderTs);
  }

  if (order.orderType === 'buffet' && isActiveBuffetSession(order)) return true;

  return false;
}

/** True when the POS sidebar should treat the order as fully paid (no Pay for balance due).
 * Active buffet sessions stay on the Pending list until ended, but they ARE settled once
 * payments cover the ticket — do not reuse isPendingPosOrder here or Pay now never clears.
 */
export function isPosOrderFullySettled(order: Order): boolean {
  if (order.status === 'cancelled' || order.status === 'refunded' || order.status === 'comped' || order.isDeleted) {
    return false;
  }
  if (!orderHasMeaningfulItems(order)) return false;
  if (order.orderType === 'uber-doordash') return order.status === 'served';
  if (hasBalanceDue(order)) return false;
  if (order.paymentStatus === 'paid') return true;
  // Dollar settlement is authoritative when paymentStatus lags behind recorded payments
  const { totalAmount } = getOrderTotals(order);
  return getAmountPaid(order) + 0.01 >= totalAmount;
}

/**
 * True when an order belongs in the POS Completed list.
 * Uber/DoorDash orders complete on handoff even if POS paymentStatus is still unpaid.
 */
export function isCompletedPosOrder(order: Order): boolean {
  if (order.status === 'cancelled' || order.status === 'refunded' || order.isDeleted) return false;
  if (!orderHasMeaningfulItems(order)) return false;

  if (order.orderType === 'uber-doordash') return order.status === 'served';

  const hasPayment = Array.isArray(order.payments) && order.payments.some((p) => p.amount > 0);
  const isPaidLike = order.paymentStatus === 'paid' || hasPayment;
  if (!isPaidLike) return false;

  if (order.orderType === 'takeaway' && order.status !== 'served') return false;
  if (order.orderType === 'online' && order.status !== 'served') return false;
  if (order.orderType === 'buffet' && order.status !== 'served') return false;
  if (order.orderType === 'event' && order.status !== 'served') return false;

  return true;
}

/**
 * Number of seats/covers used by this order for table capacity.
 * Buffet: sum of item quantities (e.g. "Lunch Buffet x 4" => 4). Otherwise 1 per order.
 */
export function getSeatsUsedByOrder(order: Order): number {
  if (order.orderType === 'buffet' && order.items?.length) {
    const total = order.items
      .filter((item) => !item.removed && (item.quantity ?? 0) > 0)
      .reduce((sum, item) => sum + (item.quantity ?? 0), 0);
    return total > 0 ? total : 1;
  }
  return 1;
}

/**
 * Seats used at a table by the given orders (e.g. active table orders).
 * Used with table.capacity to get spareSeats = capacity - seatsUsed.
 */
export function getTableSeatsUsed(orders: Order[]): number {
  return orders.reduce((sum, o) => sum + getSeatsUsedByOrder(o), 0);
}

export { isUnpaidOnlineWebOrder } from './onlineOrderVisibility';
